import z from "zod";

export const SeasonZ = z.object({
	Id: z.string(),
	Name: z.string(),
	IsCurrentSeason: z.boolean()
});

export type Season = z.infer<typeof SeasonZ>;

const OwningOrganisationZ = z.object({
	Id: z.string(),
	Name: z.string(),
	ShortName: z.string(),
	LogoUrl: z.string().optional()
});

const GradeZ = z.object({
	Id: z.string(),
	Name: z.string(),
	IsCurrent: z.boolean(),
	OwningOrganisation: OwningOrganisationZ
});
export type Grade = z.infer<typeof GradeZ>;

export const TeamZ = z.object({
	Id: z.string(),
	Name: z.string(),
	Grade: z.object({
		Id: z.string(),
		Name: z.string(),
		OwningOrganisation: OwningOrganisationZ
	}),
	Grades: GradeZ.array()
});

export type Team = z.infer<typeof TeamZ>;

export const TeamsZ = z.object({
	Teams: TeamZ.array()
});

export const PlayerStatsZ = z.object({
	Id: z.string(),
	Name: z.string(),
	Organisation: z.any(),
	ShortName: z.string()
});

export const BatStatsZ = z.object({
	Statistics: z.object({
		Matches: z.number(),
		BattingInnings: z.number(),
		BattingAggregate: z.number(),
		BattingBallsFaced: z.number(),
		Batting0s: z.number(),
		Batting50s: z.number(),
		Batting100s: z.number(),
		BattingFours: z.number(),
		BattingSixes: z.number(),
		BattingNotOuts: z.number(),
		BattingHighScore: z.number(),
		IsBattingHSNotOut: z.boolean()
	})
});

export const BowlStatsZ = z.object({
	Statistics: z.object({
		Matches: z.number(),
		BowlingBalls: z.number(),
		BowlingRuns: z.number(),
		BowlingWickets: z.number(),
		BowlingMaidens: z.number(),
		Bowling5WIs: z.number(),
		Bowling10WMs: z.number(),
		BowlingNoBalls: z.number(),
		BowlingWides: z.number(),
		BowlingBestInnings: z.string()
	})
});

export const FieldStatsZ = z.object({
	Statistics: z.object({
		FieldingAssistedRunOuts: z.number(),
		FieldingCatchesNonWK: z.number(),
		FieldingCatchesWK: z.number(),
		FieldingRunOuts: z.number(),
		FieldingStumpings: z.number(),
		FieldingTotalCatches: z.number(),
		FieldingUnassistedRunOuts: z.number(),
		Matches: z.number()
	})
});

export const PlayerBatStatsZ = PlayerStatsZ.merge(BatStatsZ);
export type PlayerBatStats = z.infer<typeof PlayerBatStatsZ>;
export const PlayerBowlStatsZ = PlayerStatsZ.merge(BowlStatsZ);
export type PlayerBowlStats = z.infer<typeof PlayerBowlStatsZ>;
export const PlayerFieldStatsZ = PlayerStatsZ.merge(FieldStatsZ);
export type PlayerFieldStats = z.infer<typeof PlayerFieldStatsZ>;

export const MatchZ = z.object({
	Id: z.string(),
	Status: z.string(),
	Teams: z.array(
		z.object({
			Id: z.string(),
			OwningOrganisation: z.object({
				Id: z.string()
			})
		})
	)
});

const BattingZ = z.object({
	BallsFaced: z.number(),
	BatInstance: z.number(),
	BatOrder: z.number(),
	BattingMinutes: z.number(),
	DismissalText: z.string().optional(),
	DismissalType: z.string().optional(),
	DismissalTypeId: z.number().optional(),
	FoursScored: z.number(),
	IsOnNonStrike: z.boolean(),
	IsOnStrike: z.boolean(),
	ParticipantId: z.string(),
	PlayerShortName: z.string(),
	RunsScored: z.number(),
	SixesScored: z.number(),
	StrikeRate: z.string()
});
const BowlingZ = z.object({
	BowlOrder: z.number(),
	Economy: z.string(),
	IsBowling: z.boolean(),
	MaidensBowled: z.number(),
	NoBalls: z.number(),
	OversBowled: z.number(),
	ParticipantId: z.string(),
	PlayerShortName: z.string(),
	RunsConceded: z.number(),
	WicketsTaken: z.number(),
	WideBalls: z.number()
});

export const ScorecardZ = z.object({
	Innings: z.array(
		z.object({
			Batting: z.array(BattingZ),
			Bowling: z.array(BowlingZ),
			Name: z.string()
		})
	),
	Round: z.object({
		ShortName: z.string()
	}),
	Teams: z
		.object({
			DisplayName: z.string(),
			OwningOrganisation: OwningOrganisationZ
		})
		.array()
});

export type Scorecard = z.infer<typeof ScorecardZ>;
